import { type CSSProperties, useRef, useState } from 'react'
import { Shimmer } from '@/components/shimmer'
import { WarikeMark, warikeMarkMask } from '@/components/warike-mark'
import { isFirstPaint, useLoaderHandoff } from '@/lib/loader-handoff'
import { cn } from '@/lib/utils'

type LoaderSize = 'page' | 'section'

const MARK_SIZE: Record<LoaderSize, string> = { page: '2rem', section: '1.5rem' }

/**
 * The mark and the word together — the brand as one lockup, with ONE band
 * sweeping across both (the shimmer's `lockup` variant). The nav cannot do this:
 * there the mark sits on the rail and the word in the menu, across a hairline,
 * so each keeps its own light (ui-app-chrome.feature). Here they share a box.
 */
export function BrandLockup({ size = 'page' }: { size?: LoaderSize }) {
  const mark = MARK_SIZE[size]
  return (
    <Shimmer
      variant="lockup"
      data-slot="brand-lockup"
      aria-hidden
      className={cn(
        'items-center gap-2.5 font-semibold text-foreground',
        size === 'page' ? 'text-3xl' : 'text-xl',
      )}
      style={{ '--shimmer-mark': warikeMarkMask, '--shimmer-mark-size': mark } as CSSProperties}
    >
      <WarikeMark className="shrink-0" style={{ width: mark, height: mark }} />
      <span>Warike</span>
    </Shimmer>
  )
}

/**
 * THE loading state for a whole app, page or section: the brand lockup with its
 * sweep, instead of a bare "Loading…" or a spinner. `page` centres it on the full
 * screen (the session check, a public page's chunk); `section` centres it in the
 * space it stands in for. Small status text inside a form or drawer stays text.
 *
 * Announced once, as "Loading": the lockup itself is decoration. Under reduced
 * motion the band stays parked and the lockup is simply still (ui-effects §10).
 *
 * It fades in when it appears — except on the app's very first paint, where it
 * is simply there (fading in from an empty page would add the blank frame the
 * fade exists to hide). With `handoff`, it also fades OUT over the page that
 * replaces it instead of cutting (lib/loader-handoff.ts): the session loader
 * and the in-shell route loader hand over; a card's loader just goes.
 */
export function BrandLoader({
  size = 'section',
  className,
  handoff = false,
}: {
  size?: LoaderSize
  className?: string
  handoff?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Fixed at mount: a re-render must never restart (or start) the fade.
  const [appear] = useState(() => !isFirstPaint())
  useLoaderHandoff(ref, handoff)
  return (
    <div
      ref={ref}
      role="status"
      aria-label="Loading"
      data-slot="brand-loader"
      data-size={size}
      data-loader-motion={appear ? 'enter' : undefined}
      className={cn(
        'grid place-items-center',
        size === 'page' ? 'min-h-svh bg-background' : 'py-16',
        className,
      )}
    >
      <BrandLockup size={size} />
      <span className="sr-only">Loading</span>
    </div>
  )
}
